import React, { useState, useRef, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronDownIcon } from './react-icons/roundedTriangle.svg';
import { ReactComponent as SearchIcon } from './react-icons/search.svg';
import './EnhancedSelect.css';

interface Option {
  value: string;
  label: string;
}

interface EnhancedSelectProperties {
  options: Option[];
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  controlId?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  keepOpenOnSelect?: boolean;
  selectedValues?: Set<string>;
  valueLabel?: string;
}

const EnhancedSelect: React.FC<EnhancedSelectProperties> = ({
  options,
  placeholder,
  value,
  onChange,
  controlId,
  ariaLabel,
  ariaLabelledBy,
  className = '',
  searchable = true,
  disabled = false,
  clearable = false,
  keepOpenOnSelect = false,
  selectedValues,
  valueLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);
  const optionsReference = useRef<HTMLDivElement>(null);

  const handleOptionSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      if (!keepOpenOnSelect) {
        setIsOpen(false);
      }
      setSearchTerm('');
      setHighlightedIndex(-1);
    },
    [keepOpenOnSelect, onChange],
  );

  // Filter options based on search term
  const filteredOptions = options.filter(
    (option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedSet = selectedValues ?? null;
  const selectedOption =
    selectedSet && selectedSet.size > 0
      ? null
      : options.find((option) => option.value === value);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectReference.current && !selectReference.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputReference.current) {
      searchInputReference.current.focus();
    }
  }, [isOpen, searchable]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          setHighlightedIndex((previous) =>
            previous < filteredOptions.length - 1 ? previous + 1 : 0,
          );
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          setHighlightedIndex((previous) =>
            previous > 0 ? previous - 1 : filteredOptions.length - 1,
          );
          break;
        }
        case 'Enter': {
          event.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        }
        case 'Escape': {
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleOptionSelect, isOpen, highlightedIndex, filteredOptions]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsReference.current) {
      const highlightedElement = optionsReference.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };
  const handleClearKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClear(event);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setHighlightedIndex(-1);
  };

  const { t } = useTranslation('common');
  return (
    <div
      ref={selectReference}
      className={`enhanced-select ${className} ${isOpen ? 'open' : ''} ${
        disabled ? 'disabled' : ''
      }`}
    >
      <button
        id={controlId}
        type="button"
        className="enhanced-select__control"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        <div className="enhanced-select__value-container">
          {valueLabel && selectedSet && selectedSet.size > 0 ? (
            <span className="enhanced-select__single-value">{valueLabel}</span>
          ) : selectedOption ? (
            <span className="enhanced-select__single-value">{selectedOption.label}</span>
          ) : (
            <span className="enhanced-select__placeholder">{placeholder}</span>
          )}
        </div>

        <div className="enhanced-select__indicators">
          {clearable && selectedOption && (
            <span
              role="button"
              tabIndex={0}
              className="enhanced-select__clear-indicator"
              onClick={handleClear}
              onKeyDown={handleClearKeyDown}
              aria-label={t('common.clearSelection')}
            >
              ×
            </span>
          )}
          <div className="enhanced-select__separator" />
          <div
            className={`enhanced-select__dropdown-indicator ${isOpen ? 'rotated' : ''}`}
            data-state={isOpen ? 'open' : 'closed'}
          >
            <ChevronDownIcon className="enhanced-select__dropdown-icon" aria-hidden="true" />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="enhanced-select__menu">
          {searchable && (
            <div className="enhanced-select__search-container">
              <SearchIcon className="enhanced-select__search-icon" />
              <input
                ref={searchInputReference}
                type="text"
                className="enhanced-select__search-input"
                placeholder={t('common.searchOptions')}
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}

          <div ref={optionsReference} className="enhanced-select__menu-list" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  type="button"
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value || selectedSet?.has(option.value)}
                  className={`enhanced-select__option ${
                    option.value === value || selectedSet?.has(option.value) ? 'selected' : ''
                  } ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleOptionSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className="enhanced-select__option-label">{option.label}</span>
                  {option.value === value || selectedSet?.has(option.value) ? (
                    <span className="enhanced-select__option-check">✓</span>
                  ) : null}
                </button>
              ))
            ) : (
              <div className="enhanced-select__no-options">{t('enhancedSelect.noOptions', 'No options found')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { EnhancedSelect };
